import React, { useState } from 'react';
import { ArrowRightIcon, CompassIcon, HeartPulseIcon, PillIcon, PresentationIcon, SlidersHorizontalIcon, SparklesIcon } from 'lucide-react';
import { Section } from '../Section';
import { InView } from '../InView';
import { SectionHeadline } from '../SectionHeadline';
import { AnimatePresence } from 'framer-motion';
import { EmbedWindow } from '../EmbedWindow';
import { illustrativeTopicInterest, signalLoop, signalMeasures } from '../../../data/deck';

/** One icon per signalMeasures entry, in order. */
const MEASURE_ICONS = [PillIcon, HeartPulseIcon, SlidersHorizontalIcon, CompassIcon, SparklesIcon];

/** Live, interactive Snak data report. */
const DEEP_DATA_URL = 'https://deepdata.snakker.io';
export function SignalsSection() {
  const [caseOpen, setCaseOpen] = useState(false);
  const maxShare = Math.max(...illustrativeTopicInterest.map(topic => topic.share));
  return <Section id="signals" data-id="element-567" data-mp="20iz7bxd9tju">
      <div className="grid items-center gap-14 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-16" data-id="element-568" data-mp="21u2zbqtb1ay">
        <div data-id="element-569" data-mp="3e8121nmgoetp">
          <InView data-id="element-570" data-mp="3e7r6lkindfgm">
            <SectionHeadline lead="More engagement." follow="More insight." data-id="element-571" data-mp="3e43i4zs27j8r" />
          </InView>
          <InView delay={0.1} data-id="element-572" data-mp="399pqn8gipw9d">
            <p className="mt-8 max-w-[40ch] text-[19px] font-semibold leading-[1.35] text-[var(--muted)]" data-id="element-573" data-mp="347qp844c99f2">
              Snakker measures more than views and clicks. Anonymous, aggregated responses make every next interaction more
              relevant.
            </p>
          </InView>
          <InView delay={0.2} className="mt-12" data-id="element-574" data-mp="3ry4drknpsq4w">
            <div className="flex flex-wrap items-center gap-2" data-id="element-575" data-mp="3rymmhgrkcwtp">
              {signalLoop.map((step, i) => <React.Fragment key={step} data-id="element-576" data-mp="3n51ldfuobmqa" data-mpi={i}>
                  <span className="rounded-full bg-white px-4 py-2 text-[12px] font-semibold text-[var(--fg)] ring-1 ring-[var(--fg)]/10" data-id="element-577" data-mp="3i8kuwf004wa1">
                    {step}
                  </span>
                  {i < signalLoop.length - 1 && <ArrowRightIcon className="h-3.5 w-3.5 text-[var(--fg)]/40" aria-hidden="true" data-id="element-578" data-mp="epzzo9m484yg" />}
                </React.Fragment>)}
            </div>
            <p className="mt-8 flex items-center gap-3 text-[24px] font-extrabold leading-tight tracking-[-0.02em] text-[var(--fg)]" data-id="element-579" data-mp="2dwyee8hgi4q3">
              <span aria-hidden="true" className="h-[2px] w-8 rounded-full bg-[var(--brand)]" data-id="element-580" data-mp="1z7iwzj3tfbn2" />
              Learn what works. Do more of it.
            </p>
            <button type="button" onClick={() => setCaseOpen(true)} className="mt-10 inline-flex items-center gap-2.5 rounded-full bg-[var(--fg)] px-6 py-3.5 text-[14px] font-bold text-white shadow-[0_18px_40px_-20px_rgba(14,27,51,0.65)] transition-colors hover:bg-[var(--brand)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" data-id="element-581" data-mp="1q7vxj2uncfwn">
              <PresentationIcon className="h-4 w-4" aria-hidden="true" data-id="element-582" data-mp="3ampi8ybn148k" />
              Proof: a Snak data report
            </button>
          </InView>
        </div>

        <InView delay={0.15} y={26} data-id="element-583" data-mp="140rsbkcu5wqa">
          <figure className="rounded-[30px] bg-[var(--fg)] p-6 text-white shadow-[0_50px_90px_-50px_rgba(14,27,51,0.8)] sm:p-8" data-id="element-584" data-mp="z16td9xo1odk">
            <div className="flex items-center justify-between" data-id="element-585" data-mp="1dqbcqug75yn9">
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--brand-lighter)]" data-id="element-586" data-mp="1drwa0rtwbvot">
                Campaign intelligence
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/40" data-id="element-587" data-mp="1mp6loeamlmu9">Illustrative</span>
            </div>

            <div className="mt-6" data-id="element-588" data-mp="1woxbw3s5lfoj">
              <p className="text-[13px] font-semibold text-white/70" data-id="element-589" data-mp="1wnw180f1uv7c">IVF Pulse · topics HCPs chose to explore</p>
              <ul className="mt-3 space-y-2" data-id="element-590" data-mp="1rr5mbgeze5ni">
                {illustrativeTopicInterest.map((topic, i) => <li key={topic.label} className="flex items-center gap-3" data-id="element-591" data-mp="2b683kwi9q10" data-mpi={i}>
                    <span className="w-[46%] shrink-0 truncate text-[12px] font-semibold text-white/70" data-id="element-592" data-mp="3ti8wk4kyli7j">{topic.label}</span>
                    <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-white/10" data-id="element-593" data-mp="2kk0tky6ox2sn">
                      <span className={`block h-full rounded-full ${i === 0 ? 'bg-[var(--brand-light)]' : 'bg-white/35'}`} style={{
                    width: `${topic.share / maxShare * 100}%`
                  }} data-id="element-594" data-mp="2phslhs7c4owu" />
                    </span>
                    <span className="w-9 shrink-0 text-right text-[12px] font-bold tabular-nums text-white" data-id="element-595" data-mp="21uu50uj7tgyx">{topic.share}%</span>
                  </li>)}
              </ul>
            </div>

            <ol className="mt-8 border-t border-white/10" data-id="element-596" data-mp="2atny4axc7p7q">
              {signalMeasures.map((output, i) => {
              const Icon = MEASURE_ICONS[i % MEASURE_ICONS.length];
              return <li key={output} className="flex items-center gap-4 border-b border-white/10 py-3.5 last:border-b-0" data-id="element-597" data-mp="1j2rwk6gq96zr" data-mpi={i}>
                    <span className="w-5 text-[10px] font-semibold tabular-nums text-white/35" data-id="element-598" data-mp="1n2s89jcfz434">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/[0.07] text-[var(--brand-lighter)]" data-id="element-599" data-mp="1amsjz63vafu8">
                      <Icon className="h-4 w-4" aria-hidden="true" data-id="element-600" data-mp="11luqviuyvsqf" />
                    </span>
                    <span className="text-[14px] leading-snug text-white/85" data-id="element-601" data-mp="wmhwmd1rnjdt">{output}</span>
                  </li>;
            })}
            </ol>
            <figcaption className="mt-4 text-[11px] leading-relaxed text-white/45" data-id="element-602" data-mp="rugl4tcf9wou">
              Illustrative example — not survey findings. All responses anonymous and reported in aggregate.
            </figcaption>
          </figure>
        </InView>
      </div>
      <AnimatePresence data-id="element-603" data-mp="dw9x04x9zb2d">
        {caseOpen && <EmbedWindow key="proof" url={DEEP_DATA_URL} title="Proof case · Snak data report" onClose={() => setCaseOpen(false)} data-id="element-604" data-mp="dy31bdkf1j77" />}
      </AnimatePresence>
    </Section>;
}